import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import guestIdentifierService from '../services/guest.service';
import tokenManager from '../api/utils/tokenManager';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [user, setUser] = useState(tokenManager.getUserData());
  const [guestIdentifier, setGuestIdentifier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const identifySession = async () => {
      setLoading(true);
      const userData = tokenManager.getUserData();
      if (userData && userData.uuid) {
        // Logged-in user: keep existing guestIdentifier so we can upgrade guest cart -> user cart
        // but do NOT use it for cart fetches (CartContext prefers userUuid when present).
        setUser(userData);
      } else {
        setUser(null);
        const guestId = await guestIdentifierService.getIdentifier();
        setGuestIdentifier(guestId);
      }
      setLoading(false);
    };

    identifySession();

    // Listen for login/logout events to re-identify
    const handleAuthChange = () => identifySession();
    window.addEventListener('userDataUpdated', handleAuthChange);
    window.addEventListener('logout', handleAuthChange);
    // Also listen to generic roleUpdated event dispatched by auth.service on login/logout
    window.addEventListener('roleUpdated', handleAuthChange);

    return () => {
      window.removeEventListener('userDataUpdated', handleAuthChange);
      window.removeEventListener('logout', handleAuthChange);
      window.removeEventListener('roleUpdated', handleAuthChange);
    };
  }, []);

  const memoizedUser = useMemo(() => user, [user]);

  const value = useMemo(() => ({
    user: memoizedUser,
    guestIdentifier,
    userUuid: memoizedUser?.uuid || "", // Empty string for guests
    // Always expose guestIdentifier as ipAddress; CartContext will only use it
    // when userUuid is not available (i.e., guest session).
    ipAddress: guestIdentifier,
    isLoggedIn: !!memoizedUser,
    loading,
  }), [memoizedUser, guestIdentifier, loading]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

